import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/** The shared detail-page card: 14px, hairline border, soft shadow. */
export const cardSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

export const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    px: 3,
    // Taller than the section headers below it — this card is the page's subject.
    py: 5,
    background: `linear-gradient(180deg, ${theme.palette.primaryColor.A50} 0%, ${theme.palette.common.white} 100%)`,
};

export const badgeSx: SxProps<Theme> = {
    width: '58px',
    height: '58px',
    borderRadius: '14px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '30px'},
};

export const titleSx: SxProps<Theme> = {
    fontSize: '1.625rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
};

export const subtitleSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.25,
};

/**
 * The contract's state, as a pill with a dot.
 *
 * It used to be a ColorField — the chip the tables use for a cell. Three states, and only one of them
 * means the contract is doing its job, so they are not all the same colour: signing is waiting on
 * someone, archived is over, operation is running.
 */
export const statusPillSx = (tone: 'running' | 'pending' | 'over'): SxProps<Theme> => {
    const tones = {
        running: {bg: '#EAF6EC', fg: theme.palette.success.dark, br: theme.palette.success.light, dot: theme.palette.success.main},
        pending: {bg: theme.palette.primaryColor.A50, fg: theme.palette.primary.dark, br: theme.palette.primaryColor.A100, dot: theme.palette.primary.main},
        over: {bg: theme.palette.neutral[50], fg: theme.palette.neutral[600], br: theme.palette.neutral[200], dot: theme.palette.neutral[400]},
    }[tone];

    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        fontSize: '0.6875rem',
        fontWeight: 600,
        px: 1.25,
        py: 0.5,
        borderRadius: '999px',
        whiteSpace: 'nowrap',
        background: tones.bg,
        color: tones.fg,
        border: `1px solid ${tones.br}`,
        '&::before': {
            content: '""',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: tones.dot,
        },
    };
};

export const factsSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

export const factSx: SxProps<Theme> = {
    px: 3,
    py: 3.5,
    borderRight: `1px solid ${theme.palette.neutral[100]}`,
    '&:last-of-type': {borderRight: 'none'},
};

export const factLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const factValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    mt: 0.4,
};

export const factLinkSx: SxProps<Theme> = {
    ...factValueSx as object,
    color: theme.palette.primary.dark,
    textDecoration: 'none',
    display: 'inline-block',
    '&:hover': {textDecoration: 'underline'},
};

/** The second line under a fact — a phone number, a street, the date a state changed. */
export const factNoteSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.4,
};
